export interface BearerJWT {
  iat: number
  exp: number
  jti: string
  scope: string[]
  role: 'personnel' | 'customer'
  broker_id: string
  id: string
}

export interface Token {
  jwt: string
  payload: BearerJWT
}

interface TokenResponse {
  jwt: string
  payload: BearerJWT
}

export interface BankIDCredentialsResponse {
  orderRef: string
  autoStartToken: string
  faultstring?: string
  qrData: string
}

export interface BankIDStatusResponse {
  status: 'pending' | 'failed' | 'complete'
  hintCode?: string
  qrData: string
}

export interface UserAuthenticatedResponse {
  user: {
    id: string
    [key: string]: unknown
  }
  jwt: string
}

export const TOKEN_UPDATED_EVENT = 'staff-token-updated'
const PERSONNEL_ROLE = 'personnel'
const API_BASE_URL = import.meta.env.VITE_AUTH_URL || ''

type RequestOptions = {
  method?: 'GET' | 'POST'
  body?: Record<string, unknown>
}

const getAuthHeader = (): Record<string, string> => {
  const token = getStoredToken()
  if (!token?.jwt) {
    return {}
  }
  return { authorization: `bearer ${token.jwt}` }
}

const request = async <T>(path: string, options: RequestOptions = {}): Promise<T> => {
  if (!API_BASE_URL) {
    throw new Error('Missing API base URL (VITE_AUTH_URL).')
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: options.method ?? 'GET',
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      ...getAuthHeader(),
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
  })

  if (!response.ok) {
    let message = 'Request failed'
    try {
      const data = (await response.json()) as { message?: string }
      if (data?.message) {
        message = data.message
      }
    } catch {
      // Ignore parse errors and keep generic message.
    }
    throw new Error(message)
  }

  return (await response.json()) as T
}

export const decodeJWT = (jwt: string): BearerJWT => {
  const base64Url = jwt.split('.')[1]
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
  const jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split('')
      .map((char) => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`)
      .join(''),
  )

  return JSON.parse(jsonPayload) as BearerJWT
}

export const getStoredToken = (): Token | null => {
  try {
    const token = window.localStorage.getItem('token')
    if (!token) {
      return null
    }
    return JSON.parse(token) as Token
  } catch {
    return null
  }
}

export const storeToken = (token: Token) => {
  window.localStorage.setItem('token', JSON.stringify(token))
  window.dispatchEvent(new Event(TOKEN_UPDATED_EVENT))
}

export const clearToken = () => {
  window.localStorage.removeItem('token')
  window.dispatchEvent(new Event(TOKEN_UPDATED_EVENT))
}

export const hasValidToken = () => {
  const token = getStoredToken()
  if (!token?.payload?.exp) {
    return false
  }

  const now = Date.now() / 1000
  return token.payload.exp > now
}

export const hasRequiredScope = (requiredScope: string) => {
  const token = getStoredToken()
  const scopes = token?.payload?.scope ?? []
  return scopes.includes(requiredScope)
}

export const hasStaffSession = () => {
  return hasValidToken() && hasRequiredScope('broker')
}

export const newAnonymousToken = async (organisationName: string) => {
  const data = await request<TokenResponse>('/auth/token', {
    method: 'POST',
    body: {
      organisation_name: organisationName,
      role: PERSONNEL_ROLE,
    },
  })

  const token = {
    jwt: data.jwt,
    payload: data.payload ?? decodeJWT(data.jwt),
  }

  storeToken(token)

  return token
}

export const startBankIDAuth = async (organisationName: string): Promise<BankIDCredentialsResponse> => {
  return await request<BankIDCredentialsResponse>('/login/bankid/auth', {
    method: 'POST',
    body: {
      device: 'desktop',
      organisation_name: organisationName,
    },
  })
}

export const pollBankID = async (orderRef: string): Promise<BankIDStatusResponse | UserAuthenticatedResponse> => {
  return await request<BankIDStatusResponse | UserAuthenticatedResponse>('/login/bankid/collect', {
    method: 'POST',
    body: {
      orderRef,
      rolesCategory: PERSONNEL_ROLE,
      requestedRole: ['*'],
    },
  })
}

export const refreshToken = async (): Promise<Token> => {
  const data = await request<TokenResponse>('/auth/token-refresh', { method: 'POST' })

  const token = {
    jwt: data.jwt,
    payload: data.payload ?? decodeJWT(data.jwt),
  }

  storeToken(token)
  return token
}

export const applyAuthenticatedJWT = (jwt: string) => {
  const payload = decodeJWT(jwt)
  storeToken({ jwt, payload })
}
